import { BlockMath } from "react-katex";
import "katex/dist/katex.min.css";

/*
 * BlogFormulaCard — exact replica of the engine page's `ModelCard`
 * so a blog post can host a formula with the same visual language:
 *
 *   • `tile` outer card (icon tile + eyebrow + title + bordered
 *     formula panel with radial glow)
 *   • `.formula-panel` KaTeX theming kicks in automatically for the
 *     electric-blue `\text{...}` labels, white equation text, etc.
 *   • Fixed 260 px height with a vertical scroll (same as engine) so
 *     tall formulas don't blow out mobile layout. Matches user's
 *     intent to allow a scroll box here.
 *
 * Props:
 *   icon      lucide-react icon (matches the companion feature card)
 *   eyebrow   small uppercase mono label (e.g. "LIVE TESTOSTERONE READOUT")
 *   title     white display title (e.g. "Live Testosterone Model")
 *   latex     KaTeX-ready LaTeX string
 *   powers    (optional) small "POWERS X" eyebrow under the panel
 *   body      (optional) description paragraph under `powers`
 */
export default function BlogFormulaCard({
    icon: Icon,
    eyebrow,
    title,
    latex,
    powers,
    body,
}) {
    return (
        <div
            className="blog-formula-card not-prose my-8 tile p-6 lg:p-7"
            data-testid="blog-formula-card"
        >
            <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-[#2ba6e0]/10 border border-[#2ba6e0]/30 flex items-center justify-center shrink-0">
                    {Icon ? <Icon size={18} className="text-[#49bef5]" /> : null}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-[#49bef5] mb-1">
                        {eyebrow}
                    </div>
                    <h3 className="font-display font-semibold text-white text-[18px] lg:text-[19px] leading-tight">
                        {title}
                    </h3>
                </div>
            </div>

            <div
                className="formula-panel relative rounded-xl border border-white/8 px-4 py-6 lg:py-7 overflow-hidden h-[790px]"
                style={{
                    background:
                        "radial-gradient(circle at 50% 50%, rgba(43,166,224,0.10), transparent 70%), #06070a",
                }}
            >
                <div className="formula-scroll absolute inset-0 overflow-y-auto overflow-x-auto px-4 py-6 lg:py-7 flex justify-center">
                    <div className="formula-content text-white/90 text-center w-full">
                        <BlockMath math={latex} />
                    </div>
                </div>
            </div>

            {powers && (
                <div className="text-[11px] uppercase tracking-[0.16em] font-mono text-white/45 mt-4 mb-2">
                    {powers}
                </div>
            )}
            {body && (
                <p className="text-white/65 text-[14px] leading-relaxed">
                    {body}
                </p>
            )}
        </div>
    );
}
